import React, { useEffect, useRef, useState } from "react";
import {
  OPERATOR,
  GRIEVANCE,
  POLICY_VERSION,
  LAST_UPDATED,
} from "../legal/legalMeta";
//import LegalNotice from "./LegalNotice";
import {
  Shield,
  Lock,
  Eye,
  Database,
  Globe,
  Mail,
  Instagram,
  Youtube,
} from "lucide-react";

import InstallAppButton from "./InstallAppButton"; //Facebook
//import { FaXTwitter } from "react-icons/fa6";

// ─── Utility: intersection-observer reveal hook ───────────────────────────
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ─── Vybex Logo ──────────────────────────────────────────────────────────────
const VybexLogo = () => {
  const ref = useRef(null);
  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Michroma&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    const container = ref.current;
    if (!container) return;
    container.innerHTML = ""; // prevent double render in StrictMode
    "VYBEX".split("").forEach((ch, i) => {
      const s = document.createElement("span");
      Object.assign(s.style, {
        fontFamily: "'Michroma', sans-serif",
        background: "linear-gradient(135deg, #00d4ff, #7c3aed, #ec4899)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        opacity: "0",
        transform: "translateY(6px) scale(0.85)",
        display: "inline-block",
        transition: `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${i * 80}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${i * 80}ms`,
      });
      s.textContent = ch;
      container.appendChild(s);
      setTimeout(
        () => {
          s.style.opacity = "1";
          s.style.transform = "translateY(0) scale(1)";
        },
        100 + i * 80,
      );
    });
  }, []);
  return (
    <span
      ref={ref}
      style={{
        display: "inline-flex",
        letterSpacing: "0.18em",
        fontSize: "0.9rem",
        background: "#0a0a18",
        padding: "4px 12px",
        borderRadius: "999px",
        border: "1px solid rgba(124,58,237,0.35)",
      }}
    />
  );
};
// ─── Footer Link ──────────────────────────────────────────────────────────
const FooterLink = ({ href, onClick, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <a
      href={href || "#"}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        color: hover ? "#7c3aed" : "#4b5563",
        textDecoration: "none",
        fontSize: "0.875rem",
        fontWeight: hover ? "600" : "400",
        cursor: "pointer",
        transition: "color 0.2s",
        display: "flex",
        alignItems: "center",
        gap: "0.4rem",
      }}
    >
      {hover && <span style={{ fontSize: "0.6rem", color: "#a78bfa" }}>▶</span>}
      {children}
    </a>
  );
};

// ─── Social Icon ──────────────────────────────────────────────────────────
const SocialIcon = ({ s }) => {
  const [hover, setHover] = useState(false);
  return (
    <a
      href={s.href}
      target="_blank"
      rel="noopener noreferrer"
      title={s.name}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 36,
        height: 36,
        borderRadius: "10px",
        border: hover ? `1.5px solid ${s.hoverBg}` : "1.5px solid #ede9fe",
        background: hover ? `${s.hoverBg}14` : "#fafafa",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1rem",
        textDecoration: "none",
        transition: "all 0.2s",
        transform: hover ? "translateY(-2px)" : "none",
      }}
    >
      {s.icon}
    </a>
  );
};

// ─── Section Card ─────────────────────────────────────────────────────────
const SectionCard = ({ section, idx }) => {
  const [ref, visible] = useReveal();
  const [expanded, setExpanded] = useState(true);

  return (
    <div
      id={section.id}
      ref={ref}
      style={{
        background: "#fff",
        border: "1.5px solid #ede9fe",
        borderRadius: "20px",
        overflow: "hidden",
        marginBottom: "1.25rem",
        boxShadow: "0 2px 16px rgba(102,126,234,0.05)",
        transform: visible ? "translateY(0)" : "translateY(24px)",
        opacity: visible ? 1 : 0,
        transition: `transform 0.65s cubic-bezier(.22,1,.36,1) ${idx * 60}ms, opacity 0.65s ease ${idx * 60}ms`,
      }}
    >
      {/* Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        className="pp-card-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "1.75rem 2rem",
          cursor: "pointer",
          background: expanded
            ? "linear-gradient(135deg,rgba(102,126,234,0.03),rgba(118,75,162,0.05))"
            : "#fff",
          borderBottom: expanded ? "1px solid #ede9fe" : "none",
          transition: "background 0.3s",
          userSelect: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background:
                "linear-gradient(135deg,rgba(102,126,234,0.1),rgba(118,75,162,0.16))",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#7c3aed",
              flexShrink: 0,
            }}
          >
            {section.icon}
          </div>
          <h2
            className="pp-card-title"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(1.1rem, 2vw, 1.35rem)",
              fontWeight: "700",
              color: "#1e1b4b",
              margin: 0,
              letterSpacing: "-0.01em",
            }}
          >
            {section.title}
          </h2>
        </div>
        <span
          style={{
            fontSize: "1.1rem",
            color: "#a78bfa",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s",
            flexShrink: 0,
          }}
        >
          ▾
        </span>
      </div>

      {/* Body */}
      {expanded && (
        <div className="pp-card-body" style={{ padding: "1.75rem 2rem" }}>
          {section.content.map((sub, si) => (
            <div
              key={si}
              style={{
                marginBottom: si < section.content.length - 1 ? "1.75rem" : 0,
              }}
            >
              {sub.subtitle && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.6rem",
                    marginBottom: "1rem",
                  }}
                >
                  <div
                    style={{
                      width: "1.25rem",
                      height: "2px",
                      background: "linear-gradient(90deg,#667eea,#764ba2)",
                      borderRadius: "2px",
                    }}
                  />
                  <h3
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: "700",
                      color: "#7c3aed",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      margin: 0,
                    }}
                  >
                    {sub.subtitle}
                  </h3>
                </div>
              )}
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  display: "grid",
                  gap: "0.5rem",
                }}
              >
                {sub.items.map((item, ii) => (
                  <li
                    key={ii}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "0.75rem",
                      padding: "0.6rem 0.85rem",
                      background: "rgba(102,126,234,0.03)",
                      borderRadius: "8px",
                      border: "1px solid rgba(196,181,253,0.3)",
                    }}
                  >
                    <span
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        flexShrink: 0,
                        background: "linear-gradient(135deg,#667eea,#764ba2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.6rem",
                        color: "#fff",
                        fontWeight: "800",
                        marginTop: "0.1rem",
                      }}
                    >
                      ✓
                    </span>
                    <span
                      style={{
                        color: "#374151",
                        lineHeight: 1.65,
                        fontSize: "0.9rem",
                      }}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Inline Footer ────────────────────────────────────────────────────────
const Footer = ({ onNavigate }) => {
  const currentYear = new Date().getFullYear();
  const handleNavigation = (view) => {
    if (onNavigate) onNavigate(view);
    else window.location.href = view.startsWith("/") ? view : `/${view}`;
  };

  const socials = [
    {
      name: "Instagram",
      icon: <Instagram size={18} color="#E1306C" />,
      href: "https://www.instagram.com/noryq.ai",
      hoverBg: "#E1306C",
    },

    {
      name: "YouTube",
      icon: <Youtube size={18} color="#aa023aff" />,
      href: "https://youtube.com/@noryq-ai",
      hoverBg: "#FF0000",
    },
  ];

  const quickLinks = [
    { name: "Home", view: "home" },
    { name: "Trending", view: "trending" },
    { name: "Premium", view: "premium" },
    { name: "About Us", href: "/about" },
    { name: "Why Us", href: "/whyus" },
  ];
  const legalLinks = [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" },
    { name: "Cookie Policy", href: "/cookies" },
    { name: "Refund Policy", href: "/refund" },
    { name: "Grievance Officer", href: "/privacy#grievance" },
    { name: "Contact Us", href: "/contact" },
  ];

  return (
    <footer
      className="pp-footer"
      style={{
        background: "#fff",
        borderTop: "1px solid #ede9fe",
        padding: "4rem 2rem 2rem",
      }}
    >
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "3rem",
            marginBottom: "3rem",
          }}
        >
          {/* Brand */}
          <div>
            <a
              href="/"
              style={{
                textDecoration: "none",
                display: "inline-block",
                marginBottom: "1rem",
              }}
            >
              <span
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: "900",
                  fontSize: "1.5rem",
                  letterSpacing: "0.2em",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {/* Logo Section - Fixed Size */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    cursor: "pointer",
                    flexShrink: 0, // Prevents logo from shrinking
                  }}
                >
                  <img
                    src="/logo.png"
                    alt="NORYQ"
                    style={{
                      height: "50px",
                      width: "auto", // Maintains aspect ratio
                      display: "block",
                    }}
                  />
                </div>
              </span>
            </a>
            <p
              style={{
                color: "#6b7280",
                lineHeight: 1.7,
                fontSize: "0.875rem",
                marginBottom: 0,
              }}
            >
              Your AI-powered movie and TV show recommendation companion. Find
              your perfect watch based on your mood.
            </p>
            {/* Install CTA card */}
            <style>{`
              @media (max-width: 480px) {
                .noryq-install-card { padding: 1rem !important; }
                .noryq-install-card p { font-size: 0.88rem !important; }
                .noryq-install-label { display: none !important; }
              }
            `}</style>
            <div
              className="noryq-install-card"
              style={{
                background:
                  "linear-gradient(135deg, rgba(102,126,234,0.06) 0%, rgba(118,75,162,0.1) 100%)",
                border: "1.5px solid #ede9fe",
                borderRadius: "16px",
                padding: "1.1rem 1.25rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.85rem",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {/* Label row */}
              <div
                className="noryq-install-label"
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              ></div>

              {/* Headline */}
              <p
                className="noryq-install-headline"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "0.95rem",
                  fontWeight: "700",
                  fontStyle: "italic",
                  color: "#1e1b4b",
                  lineHeight: 1.35,
                  margin: 0,
                }}
              >
                Take NORYQ everywhere.
                <br />
                <span
                  style={{
                    background: "linear-gradient(135deg,#667eea,#764ba2)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Install it in one tap.
                </span>
              </p>

              {/* Button — full width, never overflows */}
              <div style={{ width: "100%", minWidth: 0 }}>
                <InstallAppButton />
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                marginBottom: "1.25rem",
              }}
            >
              <div
                style={{
                  width: "1.25rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <h4
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#7c3aed",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                Quick Links
              </h4>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {quickLinks.map((link) => (
                <li key={link.name} style={{ marginBottom: "0.6rem" }}>
                  <FooterLink
                    href={link.href || "#"}
                    onClick={
                      link.view
                        ? (e) => {
                            e.preventDefault();
                            handleNavigation(link.view);
                          }
                        : undefined
                    }
                  >
                    {link.name}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                marginBottom: "1.25rem",
              }}
            >
              <div
                style={{
                  width: "1.25rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <h4
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#7c3aed",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                Legal
              </h4>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {legalLinks.map((link) => (
                <li key={link.name} style={{ marginBottom: "0.6rem" }}>
                  <FooterLink href={link.href}>{link.name}</FooterLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Stay Updated */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                marginBottom: "1.25rem",
              }}
            >
              <div
                style={{
                  width: "1.25rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <h4
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#7c3aed",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                Stay Updated
              </h4>
            </div>
            <p
              style={{
                color: "#6b7280",
                fontSize: "0.875rem",
                lineHeight: 1.7,
                marginBottom: "1rem",
              }}
            >
              Follow us for the latest recommendations and feature drops.
            </p>
            {/* Socials */}
            <div
              style={{ display: "flex", gap: "0.6rem", marginBottom: "1rem" }}
            >
              {socials.map((s) => (
                <SocialIcon key={s.name} s={s} />
              ))}
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                background:
                  "linear-gradient(135deg,rgba(102,126,234,0.07),rgba(118,75,162,0.1))",
                border: "1px solid #c4b5fd",
                borderRadius: "999px",
                padding: "0.4rem 1rem",
                marginBottom: "1.25rem",
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg,#667eea,#764ba2)",
                }}
              />
              <span
                style={{
                  fontSize: "0.75rem",
                  color: "#7c3aed",
                  fontWeight: 600,
                }}
              >
                v1.0.0 — Live
              </span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div
          style={{
            height: "1px",
            background:
              "linear-gradient(90deg, transparent, #c4b5fd 40%, #a78bfa 60%, transparent)",
            marginBottom: "1.5rem",
          }}
        />

        {/* Bottom */}
        <div
          className="pp-footer-bottom"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.75rem",
          }}
        >
          <p style={{ margin: 0, fontSize: "0.82rem", color: "#9ca3af" }}>
            © {currentYear} NORYQ. All rights reserved. Made with ❤️ in India
          </p>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              fontSize: "0.82rem",
              color: "#9ca3af",
              alignItems: "center",
            }}
          >
            <span>Powered by</span>
            <a
              href="https://vybex1.netlify.app"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <VybexLogo />
            </a>

            <span style={{ color: "#c4b5fd" }}>•</span>
            <span>v1.0.0</span>
          </div>
        </div>
        {/* <LegalNotice /> */}
      </div>
    </footer>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────
const PrivacyPolicy = () => {
  const [heroVisible, setHeroVisible] = useState(false);
  useEffect(() => {
    document.title = "Privacy Policy | NORYQ";
  }, []);
  // Deep links such as /privacy#grievance (content renders after load).
  useEffect(() => {
    const id = window.location.hash.replace("#", "");
    if (!id) return;
    const t = setTimeout(
      () =>
        document
          .getElementById(id)
          ?.scrollIntoView({ behavior: "smooth", block: "start" }),
      400,
    );
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);
  const [ctaRef, ctaVisible] = useReveal();

  const sections = [
    {
      icon: <Shield size={20} />,
      title: "1. Who Is Responsible For Your Data",
      content: [
        {
          subtitle: "Operator (Data Fiduciary)",
          items: [
            `${OPERATOR.brand} (noryq.in) is operated by ${OPERATOR.name}, ${OPERATOR.status}.`,
            `Address: ${OPERATOR.address}`,
            `Email: ${OPERATOR.email}`,
            "The same person also runs a separate project called Vybex. No user data is shared between NORYQ and Vybex.",
          ],
        },
        {
          subtitle: "Laws This Policy Follows",
          items: [
            "Information Technology Act, 2000 and the IT (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011",
            "Digital Personal Data Protection Act, 2023 and the DPDP Rules, 2025, as their provisions come into force",
          ],
        },
      ],
    },
    {
      icon: <Database size={20} />,
      title: "2. Information We Collect",
      content: [
        {
          subtitle: "What You Give Us",
          items: [
            "Name and email address when you sign up, or from your Google account if you use Google Sign-In (including your Google profile photo)",
            "Your confirmation that you are 18+ and a record of what you accepted, when, and which version",
            "Name and email if you join the Premium waitlist",
            "Name, email and message if you use the Contact form",
            "Feedback you submit (with your email if you are signed in)",
          ],
        },
        {
          subtitle: "Created When You Use NORYQ",
          items: [
            "Mood and preference selections and the recommendations you request",
            "Titles you like or save (My List), your watch history, streak and daily recommendation count",
            "If you buy Premium: a record of the order (order ID, payment ID, amount, date and Premium end date). Your card, UPI or bank details never reach us.",
          ],
        },
        {
          subtitle: "Collected Automatically",
          items: [
            "Basic technical data (IP address, browser, device, time) is received by our hosting and database providers when you use the service. We use it to keep the service secure and running.",
            "Only if you agree to Analytics: Google Analytics data such as pages and features used, device, browser and approximate location from your IP address.",
            "Only if you agree to Advertising: our advertising partner can receive device and browsing information when ads are shown.",
            "Some choices and history are saved in your browser's local storage (see our Cookie Policy).",
          ],
        },
        {
          subtitle: "What We Do Not Collect",
          items: [
            "Your password. Firebase Authentication handles it; we never see or store it.",
            "Card, UPI or bank details. When Premium launches, payments are handled by Razorpay and not stored by us.",
            "Precise GPS location, contacts, or sensitive data such as health or religion.",
          ],
        },
      ],
    },
    {
      icon: <Lock size={20} />,
      title: "3. Why We Use Your Information",
      content: [
        {
          subtitle: "To Run NORYQ",
          items: [
            "Create and secure your account",
            "Give you personalised recommendations",
            "Save your lists, history, streaks and daily limits",
          ],
        },
        {
          subtitle: "Only With Your Consent",
          items: [
            "Analytics, to understand which features are used and fix problems",
            "Advertising, to keep the free plan free",
            "Emailing you about the Premium launch if you joined the waitlist",
          ],
        },
        {
          subtitle: "Other Uses",
          items: [
            "Replying to your messages, feedback and privacy requests",
            "Preventing abuse and fraud, and fixing errors",
            "Meeting legal obligations",
          ],
        },
        {
          subtitle: "We Do Not",
          items: [
            "Sell your personal data",
            "Make automated decisions that have legal or similarly serious effects on you",
            "Track or show targeted ads to children",
          ],
        },
      ],
    },
    {
      icon: <Globe size={20} />,
      title: "4. Who We Share Data With",
      content: [
        {
          subtitle: "Service Providers (they process data for us)",
          items: [
            "Google Firebase: sign-in and database (your account, lists and history)",
            "Netlify: hosting and the server functions that run the app",
            "Google (Gemini) and Groq: AI engines that create recommendations. Each request contains your mood choices, preferences and titles you watched or liked. It does not contain your name, email or account ID.",
            "TMDB: movie and show data and posters. Your browser loads posters from TMDB's image servers, so TMDB can see your IP address. Streaming availability comes from JustWatch via TMDB.",
            "EmailJS: delivers messages sent through the Contact form to our inbox",
            "Google Fonts and Unsplash: fonts and some page images load directly from their servers, so they can see your IP address",
          ],
        },
        {
          subtitle: "Only If You Consent",
          items: [
            "Google Analytics: usage statistics, with ad personalisation turned off",
            "Advertising partner (currently HilltopAds): shows ads to free users and may set its own cookies",
          ],
        },
        {
          subtitle: "When Premium Launches",
          items: [
            "Razorpay will process payments. Your payment details go to Razorpay, not to us.",
          ],
        },
        {
          subtitle: "Legal Disclosure",
          items: [
            "We may disclose data if required by law, a court order or a government authority, or to protect rights and safety.",
          ],
        },
      ],
    },
    {
      icon: <Database size={20} />,
      title: "5. Where Data Is Stored And For How Long",
      content: [
        {
          subtitle: "Location",
          items: [
            "Our providers may store and process data on servers outside India. We use established providers and follow Indian law on cross-border transfers, including any restrictions the Government of India notifies.",
          ],
        },
        {
          subtitle: "Retention",
          items: [
            "Account, lists and history: while your account is active, and deleted when you delete your account or ask us to",
            "Waitlist details: until we tell you Premium has launched, or until you withdraw",
            "Contact messages: up to 12 months after we reply. Feedback: up to 24 months.",
            "Google Analytics data: no longer than 14 months",
            "Records we are legally required to keep (for example payment and tax records once Premium launches) are kept for as long as the law requires",
          ],
        },
      ],
    },
    {
      icon: <Lock size={20} />,
      title: "6. Security",
      content: [
        {
          subtitle: "How We Protect Your Data",
          items: [
            "Connections to NORYQ are encrypted with HTTPS/TLS",
            "Passwords are handled by Firebase Authentication, never by us",
            "Database rules let each user reach only their own record",
            "Admin tools are limited to the owner using a verified account",
            "No system is completely secure, so we cannot promise absolute security",
          ],
        },
        {
          subtitle: "If Something Goes Wrong",
          items: [
            "If a personal data breach affects you, we will tell you and the Data Protection Board of India as the law requires, and report cyber incidents to CERT-In as required.",
          ],
        },
      ],
    },
    {
      icon: <Eye size={20} />,
      title: "7. Your Rights",
      content: [
        {
          subtitle: "You Have The Right To",
          items: [
            "Know what personal data we hold about you and who we shared it with",
            "Download a copy of your data from Privacy & Data in your account menu, or ask us by email",
            "Correct, complete or update your data",
            "Erase your data: use Delete my account in Privacy & Data in your account menu (instant), or email us from your registered address and we will act within 30 days",
            "Withdraw consent at any time (use Cookie settings, or email us). Past processing stays valid, and some features may stop working.",
            "Nominate another person to exercise these rights if you die or become unable to",
            "Complain to the Data Protection Board of India once its process is in force, after first raising the issue with us",
          ],
        },
      ],
    },
    {
      icon: <Shield size={20} />,
      title: "8. Children",
      content: [
        {
          subtitle: "NORYQ Is For Adults (18+)",
          items: [
            "You must be 18 or older to create an account.",
            "We do not knowingly collect data from anyone under 18. If you are a parent and think your child has an account, email us and we will delete it.",
            'The "Family Time" mood is for adults choosing something to watch with family.',
          ],
        },
      ],
    },
    {
      id: "grievance",
      icon: <Mail size={20} />,
      title: "9. Grievance Officer",
      content: [
        {
          subtitle: "Complaints And Privacy Requests",
          items: [
            `Name: ${GRIEVANCE.name}`,
            `Designation: ${GRIEVANCE.designation}`,
            `Email: ${GRIEVANCE.email} (put "Grievance" in the subject)`,
            `Postal address: ${GRIEVANCE.address}`,
            `We acknowledge every complaint within ${GRIEVANCE.acknowledgeWithin} and resolve it within ${GRIEVANCE.resolveWithin}.`,
            "Please include your name, your registered email, what happened and what you want us to do.",
          ],
        },
      ],
    },
    {
      icon: <Mail size={20} />,
      title: "10. Changes To This Policy",
      content: [
        {
          subtitle: `Version ${POLICY_VERSION}`,
          items: [
            `This policy was last updated on ${LAST_UPDATED}.`,
            "If we change it in a material way, we will notify you in the app or by email and ask for your consent again where the law requires.",
          ],
        },
      ],
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#fafafa",
        color: "#1e1b4b",
        fontFamily: "'DM Sans', sans-serif",
        overflowX: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700;1,900&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #f5f3ff; }
        ::-webkit-scrollbar-thumb { background: #a78bfa; border-radius: 2px; }
        @keyframes pulse-dot { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes float-blob { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-18px) scale(1.04)} }

        /* ── Responsive ────────────────────────────────────────── */
        @media (max-width: 480px) { .pp-nav { padding: 0 1.25rem !important; } }

        @media (max-width: 640px) {
          .pp-hero-inner  { padding: 4.5rem 1.25rem 3.5rem !important; }
          .pp-sections    { padding: 2.5rem 1.25rem !important; }
          .pp-cta-card    { padding: 2.5rem 1.5rem !important; }
          .pp-cta-btn     { width: 100% !important; }
          .pp-footer      { padding: 3rem 1.25rem 1.5rem !important; }
          .pp-footer-bottom { flex-direction: column !important; align-items: flex-start !important; gap: 0.5rem !important; }
        }

        @media (max-width: 480px) {
          .pp-quicknav    { padding: 1rem 1.25rem !important; }
          .pp-card-header { padding: 1.25rem 1.25rem !important; }
          .pp-card-body   { padding: 1.25rem 1.25rem !important; }
        }

        @media (max-width: 400px) {
          .pp-card-title  { font-size: 1rem !important; }
          .noryq-install-label { display: none !important; }
          .noryq-install-card  { padding: 1rem !important; }
        }
      `}</style>

      {/* ── NAV ──────────────────────────────────────────────────────── */}
      <nav
        className="pp-nav"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "rgba(250,250,250,0.88)",
          backdropFilter: "blur(18px)",
          borderBottom: "1px solid #ede9fe",
          padding: "0 2.5rem",
          height: "64px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <a href="/" style={{ textDecoration: "none" }}>
          {/* Logo Section - Fixed Size */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              cursor: "pointer",
              flexShrink: 0, // Prevents logo from shrinking
            }}
          >
            <img
              src="/logo.png"
              alt="NORYQ"
              style={{
                height: "50px",
                width: "auto", // Maintains aspect ratio
                display: "block",
              }}
            />
          </div>
        </a>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          background: "#fff",
          borderBottom: "1px solid #ede9fe",
        }}
      >
        {/* Blobs */}
        <div
          style={{
            position: "absolute",
            top: "-15%",
            right: "-8%",
            width: "50vw",
            height: "50vw",
            maxWidth: "620px",
            maxHeight: "620px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(102,126,234,0.09) 0%, rgba(118,75,162,0.05) 50%, transparent 70%)",
            animation: "float-blob 8s ease-in-out infinite",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-10%",
            left: "-6%",
            width: "36vw",
            height: "36vw",
            maxWidth: "460px",
            maxHeight: "460px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(118,75,162,0.06) 0%, transparent 70%)",
            animation: "float-blob 11s ease-in-out infinite reverse",
            pointerEvents: "none",
          }}
        />
        {/* Dot grid */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            backgroundImage:
              "radial-gradient(circle, #d8d0f8 1px, transparent 1px)",
            backgroundSize: "28px 28px",
            opacity: 0.35,
          }}
        />

        <div
          className="pp-hero-inner"
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            padding: "7rem 2rem 6rem",
            position: "relative",
            zIndex: 1,
            textAlign: "center",
          }}
        >
          {/* Badge */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              background:
                "linear-gradient(135deg,rgba(102,126,234,0.07),rgba(118,75,162,0.12))",
              border: "1px solid #c4b5fd",
              borderRadius: "999px",
              padding: "0.4rem 1.1rem",
              marginBottom: "2rem",
              transform: heroVisible ? "translateY(0)" : "translateY(14px)",
              opacity: heroVisible ? 1 : 0,
              transition: "transform 0.6s ease 0.1s, opacity 0.6s ease 0.1s",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "linear-gradient(135deg,#667eea,#764ba2)",
                animation: "pulse-dot 1.8s infinite",
              }}
            />
            <span
              style={{
                fontSize: "0.74rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#7c3aed",
                fontWeight: 700,
              }}
            >
              Transparency First
            </span>
          </div>

          <h1
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(2.6rem, 6vw, 5rem)",
              fontWeight: "900",
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
              color: "#1e1b4b",
              marginBottom: "1.5rem",
              transform: heroVisible ? "translateY(0)" : "translateY(22px)",
              opacity: heroVisible ? 1 : 0,
              transition:
                "transform 0.75s cubic-bezier(.22,1,.36,1) 0.2s, opacity 0.75s ease 0.2s",
            }}
          >
            Your Privacy,
            <br />
            <span
              style={{
                fontStyle: "italic",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Our Priority.
            </span>
          </h1>

          <p
            style={{
              fontSize: "clamp(0.95rem, 1.8vw, 1.1rem)",
              lineHeight: 1.8,
              color: "#6b7280",
              maxWidth: "500px",
              margin: "0 auto 2rem",
              transform: heroVisible ? "translateY(0)" : "translateY(18px)",
              opacity: heroVisible ? 1 : 0,
              transition: "transform 0.7s ease 0.35s, opacity 0.7s ease 0.35s",
            }}
          >
            Here's exactly what data we collect, why, who sees it, and how you
            stay in control. Written to meet Indian law, in plain language.
          </p>

          {/* Last updated pill */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              background: "rgba(102,126,234,0.04)",
              border: "1px solid #ede9fe",
              borderRadius: "999px",
              padding: "0.35rem 1rem",
              transform: heroVisible ? "translateY(0)" : "translateY(14px)",
              opacity: heroVisible ? 1 : 0,
              transition: "transform 0.6s ease 0.5s, opacity 0.6s ease 0.5s",
            }}
          >
            <span style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
              Last updated:{" "}
              <span style={{ color: "#7c3aed", fontWeight: 600 }}>
                {LAST_UPDATED}
              </span>
            </span>
          </div>
        </div>

        <div
          style={{
            height: "2px",
            background:
              "linear-gradient(90deg, transparent, #c4b5fd 40%, #a78bfa 60%, transparent)",
          }}
        />
      </div>

      {/* ── QUICK NAV PILLS ──────────────────────────────────────────── */}
      <div
        className="pp-quicknav"
        style={{
          background: "#fff",
          borderBottom: "1px solid #ede9fe",
          padding: "1.25rem 2rem",
          overflowX: "auto",
        }}
      >
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            display: "flex",
            gap: "0.6rem",
            flexWrap: "wrap",
          }}
        >
          {[
            "Data We Collect",
            "How We Use It",
            "Security",
            "Third Parties",
            "Your Rights",
            "Contact",
          ].map((label, i) => (
            <button
              key={i}
              onClick={() =>
                document
                  .querySelectorAll("[data-section]")
                  [i]?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
              style={{
                background: "rgba(102,126,234,0.05)",
                border: "1px solid #ede9fe",
                borderRadius: "999px",
                padding: "0.4rem 1rem",
                fontSize: "0.78rem",
                color: "#7c3aed",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                letterSpacing: "0.04em",
                transition: "all 0.2s",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  "linear-gradient(135deg,rgba(102,126,234,0.1),rgba(118,75,162,0.14))";
                e.currentTarget.style.borderColor = "#c4b5fd";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(102,126,234,0.05)";
                e.currentTarget.style.borderColor = "#ede9fe";
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── SECTIONS ─────────────────────────────────────────────────── */}
      <div
        className="pp-sections"
        style={{ maxWidth: "900px", margin: "0 auto", padding: "4rem 2rem" }}
      >
        {sections.map((section, idx) => (
          <div key={idx} data-section={idx}>
            <SectionCard section={section} idx={idx} />
          </div>
        ))}

        {/* ── CTA ──────────────────────────────────────────────────── */}
        <div
          ref={ctaRef}
          className="pp-cta-card"
          style={{
            background: "#fff",
            border: "1.5px solid #ede9fe",
            borderRadius: "20px",
            padding: "3.5rem 2.5rem",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 4px 40px rgba(102,126,234,0.08)",
            marginTop: "2rem",
            transform: ctaVisible ? "translateY(0)" : "translateY(24px)",
            opacity: ctaVisible ? 1 : 0,
            transition:
              "transform 0.7s cubic-bezier(.22,1,.36,1), opacity 0.7s ease",
          }}
        >
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: "70%",
              height: "100px",
              background:
                "radial-gradient(ellipse, rgba(118,75,162,0.07) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          {/* Shield icon */}
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "16px",
              margin: "0 auto 1.5rem",
              background:
                "linear-gradient(135deg,rgba(102,126,234,0.1),rgba(118,75,162,0.16))",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#7c3aed",
            }}
          >
            <Shield size={24} />
          </div>
          <h3
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(1.4rem, 3vw, 2rem)",
              fontWeight: "900",
              letterSpacing: "-0.02em",
              color: "#1e1b4b",
              marginBottom: "0.75rem",
            }}
          >
            Questions about your{" "}
            <span
              style={{
                fontStyle: "italic",
                background: "linear-gradient(135deg,#667eea,#764ba2)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              privacy?
            </span>
          </h3>
          <p
            style={{
              color: "#6b7280",
              lineHeight: 1.7,
              maxWidth: "400px",
              margin: "0 auto 2rem",
              fontSize: "0.95rem",
            }}
          >
            We acknowledge every privacy request within 24 hours and resolve it
            within 15 days. You can also reach our Grievance Officer directly.
          </p>
          <a href="/contact" style={{ textDecoration: "none" }}>
            <button
              className="pp-cta-btn"
              style={{
                background: "linear-gradient(135deg,#667eea,#764ba2)",
                color: "#fff",
                border: "none",
                padding: "1rem 2.5rem",
                borderRadius: "12px",
                fontSize: "0.9rem",
                fontWeight: "700",
                letterSpacing: "0.06em",
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                boxShadow: "0 8px 28px rgba(102,126,234,0.28)",
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 16px 44px rgba(118,75,162,0.38)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 8px 28px rgba(102,126,234,0.28)";
              }}
            >
              Contact Privacy Team →
            </button>
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
